'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { Instagram, Music2, Youtube, Twitter, Linkedin, AtSign, Pin, Facebook, Star } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'
import { ExplainerFrame } from './explainer-frame'

// Nine sources, one moderated feed, one script tag. The badges settle
// into a ring around the widget frame while the tiles fill in, so the
// eye reads "many inputs, one output" without a single arrow.

const SOURCES = [
  { icon: Instagram, label: 'Instagram' },
  { icon: Music2,    label: 'TikTok' },
  { icon: Youtube,   label: 'YouTube' },
  { icon: Twitter,   label: 'X' },
  { icon: Linkedin,  label: 'LinkedIn' },
  { icon: AtSign,    label: 'Threads' },
  { icon: Pin,       label: 'Pinterest' },
  { icon: Facebook,  label: 'Facebook' },
  { icon: Star,      label: 'Google Reviews' },
] as const

export function SourcesToWidgetExplainer({ className }: { className?: string }) {
  return (
    <ExplainerFrame
      eyebrow="Sources"
      title="Nine sources, one feed, one script"
      caption="Connect any mix of social and review sources. Moderation, rights and product tags apply once; every layout reads the same feed."
      className={className}
    >
      {({ play, reduce, d }) => (
        <LazyMotion features={domAnimation}>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            <ul className="grid grid-cols-3 gap-2">
              {SOURCES.map((s, i) => {
                const Icon = s.icon
                return (
                  <m.li
                    key={s.label}
                    className="flex flex-col items-center gap-1 rounded-xl border bg-secondary/30 px-2 py-2.5 text-center"
                    initial={reduce ? false : { opacity: 0, x: 14 }}
                    animate={play ? { opacity: 1, x: 0 } : undefined}
                    transition={{ ...SPRING, delay: d(0.15 + i * 0.07) }}
                  >
                    <Icon className="h-4 w-4 text-primary" aria-hidden />
                    <span className="text-[10px] font-medium leading-tight text-foreground">{s.label}</span>
                  </m.li>
                )
              })}
            </ul>

            <div className="rounded-2xl border bg-card p-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Gallery · homepage</p>
                <m.span
                  className="rounded-md bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-foreground"
                  initial={reduce ? false : { opacity: 0 }}
                  animate={play ? { opacity: 1 } : undefined}
                  transition={{ duration: 0.3, delay: d(1.5) }}
                >
                  {'<script src="…/idukki.js">'} · 37 KB
                </m.span>
              </div>
              <ul className="mt-2 grid grid-cols-3 gap-1.5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <m.li
                    key={i}
                    className="relative aspect-square overflow-hidden rounded-lg bg-gradient-to-br from-secondary via-primary/10 to-primary/30"
                    initial={reduce ? false : { opacity: 0, scale: 0.94 }}
                    animate={play ? { opacity: 1, scale: 1 } : undefined}
                    transition={{ duration: 0.35, ease: EASE, delay: d(0.85 + i * 0.09) }}
                  >
                    <span className="absolute bottom-1 left-1 rounded-full bg-card/90 px-1.5 py-0.5 text-[9px] font-medium text-foreground">
                      {SOURCES[i % SOURCES.length].label}
                    </span>
                  </m.li>
                ))}
              </ul>
            </div>
          </div>
        </LazyMotion>
      )}
    </ExplainerFrame>
  )
}
